/**
 * Drill-down (design §1): the cases (or visits, WOs, ...) behind one number, in a right-side Drawer over the report.
 * The drawer is URL-synced (`?drill=<metric>&dp=k:v`), so a drill is a shareable link; the filter bar applies to it.
 */
import { Button, Drawer, EmptyState, SkeletonRows } from '@patchbay/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { DrillPage, DrillRow, ReportFilters } from '../../lib/api/generated'
import { drillQuery, type NativeReportKey } from './api'
import { formatValue, StateText } from './charts'
import type { CanSee } from './roles'
import { AppLink, dateLabel, QueryError } from './shared'

function rowValue(row: DrillRow): string | null | undefined {
  if (row.money_value !== undefined && row.money_value !== null) return row.money_value
  if (row.cost_value !== undefined && row.cost_value !== null) return row.cost_value
  return row.value
}

/**
 * Money a role may not see is absent from the payload (role_response). The column is hidden for that role rather than
 * rendered as a column of blanks: a money drill shows its value column only to roles that may see money and only when
 * the rows actually carry a money or cost slot.
 */
export function showValueColumn(page: Pick<DrillPage, 'unit' | 'items'>, canSee: CanSee): boolean {
  if (!page.unit) return page.items.some((r) => r.value !== undefined && r.value !== null)
  if (page.unit !== 'money') return true
  if (!canSee('money')) return false
  // A slot the role may see that carries a value (a money role is sent `money_value: null` for a row with none), or a
  // row stating why it has none. Otherwise the column would be all blanks.
  return page.items.some(
    (r) => (r.money_value != null && canSee('money')) || (r.cost_value != null && canSee('cost')) || (r.state != null && r.state !== 'ok'),
  )
}

function RowTitle({ row }: { row: DrillRow }) {
  if (row.case) {
    return (
      <Link to="/case/$id" params={{ id: row.case.id }} className="font-medium text-primary hover:underline">
        <span className="tabular mr-1.5 font-mono text-xs text-muted-foreground">{row.case.number}</span>
        {row.case.title}
      </Link>
    )
  }
  if (row.href) {
    return (
      <AppLink href={row.href} className="font-medium text-primary hover:underline">
        {row.title}
      </AppLink>
    )
  }
  return <span className="font-medium">{row.title}</span>
}

export function DrillTable({ pages, canSee }: { pages: DrillPage[]; canSee: CanSee }) {
  const first = pages[0]
  if (!first) return null
  const items = pages.flatMap((p) => p.items)
  const unit = first.unit ?? null
  const merged = { unit, items }
  const showValue = showValueColumn(merged, canSee)
  if (items.length === 0) {
    return <EmptyState title="No cases behind this number." detail="The filters and window above match nothing for this slice." />
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
            <th scope="col" className="py-2 pr-3">
              {first.grain === 'case' ? 'Case' : 'Item'}
            </th>
            <th scope="col" className="py-2 pr-3">
              Customer
            </th>
            <th scope="col" className="py-2 pr-3">
              Owner
            </th>
            {showValue ? (
              <th scope="col" className="py-2 pr-3 text-right">
                Value
              </th>
            ) : null}
            <th scope="col" className="py-2 text-right">
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={`${row.grain}:${row.id}`} className="border-b border-border/60 align-top">
              <td className="py-2 pr-3">
                <RowTitle row={row} />
                {row.subtitle ? <div className="text-xs text-muted-foreground">{row.subtitle}</div> : null}
              </td>
              <td className="py-2 pr-3 text-muted-foreground">{row.customer ?? '—'}</td>
              <td className="py-2 pr-3 text-muted-foreground">{row.owner?.name ?? 'Unassigned'}</td>
              {showValue ? (
                <td className="tabular py-2 pr-3 text-right">
                  {row.state && row.state !== 'ok' ? (
                    <StateText state={row.state} />
                  ) : (
                    formatValue(row.unit ?? unit ?? 'count', rowValue(row))
                  )}
                </td>
              ) : null}
              <td className="tabular whitespace-nowrap py-2 text-right text-muted-foreground">{dateLabel(row.occurred_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function DrillDrawer({
  report,
  metric,
  params,
  filters,
  canSee,
  onClose,
}: {
  report: NativeReportKey
  metric: string
  params: string[]
  filters: ReportFilters
  canSee: CanSee
  onClose: () => void
}) {
  const query = useInfiniteQuery(drillQuery(report, metric, params, filters))
  const first = query.data?.pages[0]
  const shown = query.data?.pages.reduce((n, p) => n + p.items.length, 0) ?? 0
  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      width="lg"
      title={first?.title ?? 'Loading the cases behind this number…'}
      description={first ? `${shown} of ${first.total} shown` : undefined}
      footer={
        query.hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetchingNextPage}
            disabledReason="Loading the next page"
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        ) : undefined
      }
    >
      <div className="reports-root">
        {query.data ? (
          <>
            <DrillTable pages={query.data.pages} canSee={canSee} />
            {query.isFetchNextPageError ? (
              <QueryError error={query.error} what="The next page" onRetry={() => void query.fetchNextPage()} />
            ) : query.isRefetchError ? (
              <QueryError error={query.error} what="Refreshing this drill-down" onRetry={() => void query.refetch()} />
            ) : null}
          </>
        ) : query.isError ? (
          <QueryError error={query.error} what="This drill-down" onRetry={() => void query.refetch()} />
        ) : (
          <SkeletonRows count={8} />
        )}
      </div>
    </Drawer>
  )
}
